import type { FC } from "react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { dispatchTrackingEvent, GTMEVARunner, TrackingEvents } from "@jti/tracker";

import { ProductCardContainerContext } from "../ProductCardContainerContextProvider/ProductCardContainerContextProvider";

import { viewItemListEventDataFactory } from "./utils/viewItemListEventDataFactory";

const GTM_READY_POLL_INTERVAL_MS = 500;
const REGISTRATION_TIMEOUT_MS = 5000;

type ProductCardContainerViewItemListEventDispatcherProps = {
  isEnabled: boolean;
  expectedItemsCount: number;
};

export const ProductCardContainerViewItemListEventDispatcher: FC<
  ProductCardContainerViewItemListEventDispatcherProps
> = ({ isEnabled, expectedItemsCount }) => {
  const { productsSkus, products, currency, isProductsDataLoading } = useContext(ProductCardContainerContext);
  const hasDispatchedRef = useRef(false);
  const [isGTMInitialized, setIsGTMInitialized] = useState(() => Boolean(GTMEVARunner.isReadyToAcceptEvents));
  const [hasRegistrationTimedOut, setHasRegistrationTimedOut] = useState(false);

  const skus = useMemo(() => Object.keys(productsSkus), [productsSkus]);
  const isRegistrationIncomplete =
    !isProductsDataLoading && Boolean(currency) && skus.length > 0 && skus.length < expectedItemsCount;

  const isDataReady =
    !isProductsDataLoading &&
    Boolean(currency) &&
    skus.length > 0 &&
    (skus.length >= expectedItemsCount || hasRegistrationTimedOut);

  useEffect(() => {
    if (!isEnabled || !isRegistrationIncomplete || hasRegistrationTimedOut) return;

    const timeoutId = setTimeout(() => {
      console.warn(
        `ProductCardContainer: expected ${expectedItemsCount} product card(s) to register for the ` +
          `view_item_list event, but only ${skus.length} registered. Dispatching with the available data.`
      );
      setHasRegistrationTimedOut(true);
    }, REGISTRATION_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [expectedItemsCount, hasRegistrationTimedOut, isEnabled, isRegistrationIncomplete, skus.length]);

  useEffect(() => {
    if (isGTMInitialized || !isEnabled) return;

    const intervalId = setInterval(() => {
      if (GTMEVARunner.isReadyToAcceptEvents) {
        setIsGTMInitialized(true);
      }
    }, GTM_READY_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isEnabled, isGTMInitialized]);

  useEffect(() => {
    if (hasDispatchedRef.current || !isEnabled || !isGTMInitialized || !isDataReady) return;

    const items = skus.map((sku) => ({
      sku,
      name: productsSkus[sku]?.name ?? products[sku]?.productData?.name,
      price: products[sku]?.currentPrice,
      discount: products[sku]?.discount,
      category: productsSkus[sku]?.categoryName,
    }));

    dispatchTrackingEvent(TrackingEvents.TrackingViewItemList, viewItemListEventDataFactory(items, currency ?? ""));
    hasDispatchedRef.current = true;
  }, [currency, isDataReady, isEnabled, isGTMInitialized, products, productsSkus, skus]);

  return null;
};
